import {Track} from '@app/web-player/tracks/track';
import {useTrans} from '@ui/i18n/use-trans';
import React, {useContext, useState} from 'react';
import {TableContext} from '@common/ui/tables/table-context';
import {trackToMediaItem} from '@app/web-player/tracks/utils/track-to-media-item';
import {message} from '@ui/i18n/message';
import {PauseIcon} from '@ui/icons/material/Pause';
import {PlayArrowFilledIcon} from '@app/web-player/tracks/play-arrow-filled';
import clsx from 'clsx';
import {usePlayerActions} from '@common/player/hooks/use-player-actions';
import {usePlayerStore} from '@common/player/hooks/use-player-store';
import {useTrackTableMeta} from '@app/web-player/tracks/track-table/use-track-table-meta';
import {EqualizerImage} from '@app/web-player/tracks/equalizer-image/equalizer-image';
import {useIsTrackPlaying} from '@app/web-player/tracks/hooks/use-is-track-playing';
import {useIsTrackCued} from '@app/web-player/tracks/hooks/use-is-track-cued';
import {AppHaptics} from '@common/core/capacitor/app-haptics';

interface TogglePlaybackColumnProps {
  track: Track;
  rowIndex: number;
  isHovered: boolean;
}
export function TogglePlaybackColumn({
  track,
  rowIndex,
  isHovered,
}: TogglePlaybackColumnProps) {
  const {queueGroupId, resolveQueueGroupId} = useTrackTableMeta();
  const resolvedQueueGroupId =
    resolveQueueGroupId?.(track, rowIndex) ?? queueGroupId;
  const isPlaying = useIsTrackPlaying(track.id, resolvedQueueGroupId);
  const isCued = useIsTrackCued(track.id, resolvedQueueGroupId);

  return (
    <div className="h-24 w-24 text-center">
      {isHovered || isPlaying ? (
        <TogglePlaybackButton
          track={track}
          trackIndex={rowIndex}
          isPlaying={isPlaying}
        />
      ) : (
        <span className={clsx(isCued ? 'text-primary' : 'text-muted')}>
          {rowIndex + 1}
        </span>
      )}
    </div>
  );
}

interface TogglePlaybackButtonProps {
  track: Track;
  trackIndex: number;
  isPlaying: boolean;
}
function TogglePlaybackButton({
  track,
  trackIndex,
  isPlaying,
}: TogglePlaybackButtonProps) {
  const {trans} = useTrans();
  const player = usePlayerActions();
  const controlsLocked = usePlayerStore(s => s.controlsLocked);
  const {data} = useContext(TableContext);
  const {queueGroupId, resolveQueueGroupId, onTrackPlay} = useTrackTableMeta();
  const resolvedQueueGroupId =
    resolveQueueGroupId?.(track, trackIndex) ?? queueGroupId;
  const [isHover, setHover] = useState(false);

  if (isPlaying) {
    return (
      <button
        disabled={controlsLocked}
        onPointerEnter={() => setHover(true)}
        onPointerLeave={() => setHover(false)}
        aria-label={trans(message('Pause :name', {values: {name: track.name}}))}
        tabIndex={0}
        onClick={e => {
          e.stopPropagation();
          void AppHaptics.impactLight();
          player.pause();
        }}
      >
        {isHover ? <PauseIcon /> : <EqualizerImage />}
      </button>
    );
  }

  return (
    <button
      disabled={controlsLocked}
      aria-label={trans(message('Play :name', {values: {name: track.name}}))}
      tabIndex={0}
      onClick={async e => {
        e.stopPropagation();
        void AppHaptics.impactLight();
        if (onTrackPlay) {
          onTrackPlay(track, trackIndex);
          return;
        }
        const newQueue = data.map(d =>
          trackToMediaItem(d as Track, resolvedQueueGroupId),
        );
        player.overrideQueueAndPlay(newQueue, trackIndex);
      }}
    >
      <PlayArrowFilledIcon />
    </button>
  );
}
